import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HouseholdProvider, useHousehold } from './contexts/HouseholdContext';
import { PremiumProvider } from './contexts/PremiumContext';
import Navigation from './components/Navigation';
import ListPage from './pages/ListPage';
import ShoppingPage from './pages/ShoppingPage';
import SettingsPage from './pages/SettingsPage';
import CatalogPage from './pages/CatalogPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { UndoToastProvider } from './components/UndoToast';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AppIconProvider } from './contexts/AppIconContext';
import HouseholdSetupPage from './pages/HouseholdSetupPage';
import { warmupBarcodeScanner } from './native';
import { PreferencesProvider } from './contexts/PreferencesContext';

// ── Offline / server-nede overlay ────────────────────────────────────────
function OfflineOverlay({ onRetry }) {
  const { t } = useTranslation();
  const online = navigator.onLine;
  const hasCustomServer = !!localStorage.getItem('freshtrack_server_url');

  const switchToOfficial = () => {
    localStorage.removeItem('freshtrack_server_url');
    window.location.reload();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'var(--bg-base, #0f1a19)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px', gap: '12px',
    }}>
      <div style={{ fontSize: '48px' }}>{online ? '🔧' : '📡'}</div>
      <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--fg-1, #f0f4f3)', textAlign: 'center' }}>
        {online ? t('offline.cantReach') : t('offline.noConnection')}
      </div>
      <div style={{ fontSize: '14px', color: 'var(--fg-3, #8a9e9c)', textAlign: 'center', maxWidth: '280px', lineHeight: '1.5' }}>
        {online ? t('offline.maintenance') : t('offline.checkConnection')}
      </div>
      <button onClick={onRetry} style={{
        marginTop: '8px', padding: '12px 32px',
        background: 'var(--brand, #4CAF82)', color: 'white',
        border: 'none', borderRadius: '12px',
        fontSize: '15px', fontWeight: '600', cursor: 'pointer',
      }}>
        {t('offline.retry')}
      </button>
      {online && hasCustomServer && (
        <button onClick={switchToOfficial} style={{
          padding: '10px 24px',
          background: 'none', color: 'var(--fg-3, #8a9e9c)',
          border: '1px solid var(--border-subtle, #2a3a38)', borderRadius: '12px',
          fontSize: '13px', cursor: 'pointer',
        }}>
          {t('offline.switchOfficial')}
        </button>
      )}
    </div>
  );
}

// ── Indre app — kræver login + husstand ───────────────────────────────────
function AppShell() {
  const { t } = useTranslation();
  const { user, loading: authLoading, logout } = useAuth();
  const { theme } = useTheme();
  const { activeHousehold, households, loading: hhLoading } = useHousehold();

  if (authLoading || hhLoading) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-theme={theme || 'dark'}>
      <div style={{color: 'var(--fg-3)', fontSize: '17px', fontFamily: 'Manrope, sans-serif'}} className="animate-pulse">{t('common.loading')}</div>
    </div>
  );

  if (!user) return <LoginPage />;

  // Bruger er logget ind men har ingen husstande endnu
  if (households.length === 0) return <HouseholdSetupPage />;

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-slate-900 text-white flex flex-col" data-theme={theme}>
        <div className="flex-1 overflow-hidden">
          <Routes>
            <Route path="/"              element={<Navigate to="/fryser" replace />} />
            <Route path="/fryser"        element={<ListPage listType="freezer" />} />
            <Route path="/koeleskab"      element={<ListPage listType="fridge" />} />
            <Route path="/kolonial"      element={<ListPage listType="pantry" />} />
            <Route path="/indkoeb"        element={<ShoppingPage />} />
            <Route path="/katalog"       element={<CatalogPage />} />
            <Route path="/indstillinger" element={<SettingsPage onLogout={logout} />} />
          </Routes>
        </div>
        <Navigation />
      </div>
    </BrowserRouter>
  );
}

// ── Rod-app — wrapper med providers ──────────────────────────────────────
export default function App() {
  const [networkError, setNetworkError] = useState(false);

  // Præ-varm ML Kit Vision framework ved app-start så første scan er hurtig
  useEffect(() => { warmupBarcodeScanner(); }, []);

  useEffect(() => {
    const onError  = () => setNetworkError(true);
    const onOnline = () => setNetworkError(false);
    window.addEventListener('kitchey:network-error', onError);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('kitchey:network-error', onError);
      window.removeEventListener('online', onOnline);
    };
  }, []);
  // Reset-password siden skal være tilgængelig uden login
  if (window.location.pathname === '/reset-password') {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <ErrorBoundary>
    <ThemeProvider>
    <AppIconProvider>
      {networkError && <OfflineOverlay onRetry={() => setNetworkError(false)} />}
    <AuthProvider>
      <PreferencesProvider>
      <HouseholdProvider>
        <PremiumProvider>
          <UndoToastProvider />
          <AppShell />
        </PremiumProvider>
      </HouseholdProvider>
      </PreferencesProvider>
    </AuthProvider>
    </AppIconProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}
